import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { PRICING } from "@/data/content";
import { TEST_IDS } from "@/constants/testIds";

export default function Pricing() {
  return (
    <section
      id="pricing"
      data-testid={TEST_IDS.pricing.root}
      className="py-24 md:py-40 border-t border-[#E5E1DA] bg-[#F3EFE9]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="mb-16 md:mb-24 grid md:grid-cols-12 gap-6 items-end">
          <div className="md:col-span-2 eyebrow">Pricing</div>
          <h2 className="md:col-span-8 font-serif-display font-black tracking-tight text-[clamp(38px,6vw,84px)] leading-[0.95]">
            Simple plans.
            <br />
            <span className="italic text-[#E07A5F]">No awkward calls.</span>
          </h2>
          <div className="md:col-span-2 text-right hidden md:block font-mono-ui text-[11px] tracking-[0.24em] text-[#787672] uppercase">
            Month-to-month
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {PRICING.map((tier, i) => {
            const isHi = tier.highlight;
            return (
              <motion.article
                key={tier.slug}
                data-testid={TEST_IDS.pricing.card(tier.slug)}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.8, delay: i * 0.08, ease: [0.2, 0.8, 0.2, 1] }}
                className={`relative rounded-3xl p-8 md:p-10 flex flex-col ${
                  isHi
                    ? "bg-[#1C1C1A] text-[#FDFCFB] shadow-[0_40px_80px_-30px_rgba(28,28,26,0.45)]"
                    : "bg-[#FDFCFB] border border-[#E5E1DA]"
                }`}
              >
                {isHi && (
                  <span className="absolute -top-3 left-8 bg-[#E07A5F] text-white font-mono-ui text-[10px] tracking-[0.24em] uppercase px-3 py-1 rounded-full">
                    {tier.badge}
                  </span>
                )}
                <div className="flex items-baseline justify-between">
                  <h3 className={`font-serif-display text-3xl ${isHi ? "" : "text-[#1C1C1A]"}`}>
                    {tier.name}
                  </h3>
                  <span className={`eyebrow ${isHi ? "!text-white/60" : ""}`}>
                    {tier.slug === "growth" ? "Popular" : tier.slug === "ministry-plus" ? "Advanced" : "Essentials"}
                  </span>
                </div>

                <div className="mt-6 flex items-baseline gap-2">
                  <span className={`font-serif-display text-5xl md:text-6xl tracking-tight ${isHi ? "" : "text-[#1C1C1A]"}`}>
                    {tier.price}
                  </span>
                  <span className={`text-sm ${isHi ? "text-white/60" : "text-[#787672]"}`}>{tier.cadence}</span>
                </div>

                <div className={`mt-2 text-[15px] ${isHi ? "text-white/80" : "text-[#1C1C1A]/75"}`}>
                  {tier.clips} · {tier.turnaround}
                </div>

                <div className={`my-6 h-px ${isHi ? "bg-white/15" : "bg-[#E5E1DA]"}`} />

                <ul className="space-y-3 mb-8">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-[15px] leading-relaxed">
                      <span
                        className={`mt-1 grid place-items-center w-4 h-4 rounded-full ${
                          isHi ? "bg-white/15 text-white" : "bg-[#E07A5F]/15 text-[#E07A5F]"
                        }`}
                      >
                        <Check size={11} strokeWidth={3} />
                      </span>
                      <span className={isHi ? "text-white/90" : "text-[#1C1C1A]/85"}>{f}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href={`#contact?plan=${tier.slug}`}
                  data-testid={TEST_IDS.pricing.cta(tier.slug)}
                  className={`btn-pill mt-auto justify-center ${
                    isHi
                      ? "bg-[#E07A5F] text-white hover:bg-[#FDFCFB] hover:text-[#1C1C1A]"
                      : "btn-primary"
                  }`}
                >
                  Start with {tier.name}
                  <ArrowUpRight size={16} />
                </a>
              </motion.article>
            );
          })}
        </div>

        <p className="mt-10 text-center text-[13px] text-[#787672]">
          Need something custom? We happily build one-off packages for
          conferences, revivals, and multi-campus weekends.
        </p>
      </div>
    </section>
  );
}
